import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildChartDates } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { collapseStatusOptions } from '@/features/shared/utils/projectStatusPresentation';
import { queryKeys, useIsAuthenticated, useTagOptions } from '@/shared/hooks';
import { buildDateRangeFilters, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { useEntityTrendsMetricDisplay } from './useEntityTrendsMetricDisplay';
import {
  buildChartFilterIndices,
  buildLineChartDataOrFallback,
  resolveChartDateBounds,
  filterChartRowsByIndices,
  filterSeriesByIndices,
  shouldShowSingleDayChartPoints,
} from './useEntityTrendsPage.helpers';
import {
  collapseGroupedStackedChartData,
  formatRelatedEntitySummaryTotalLabel,
} from './useEntityTrendsPage.statusPresentation';
import { useEntityTrendsQueryModel } from './useEntityTrendsQueryModel';
import { useEntityTrendsSummary } from './useEntityTrendsSummary';
import { useEntityTrendsUploadFlow } from './useEntityTrendsUploadFlow';
import { useProjectStatusPresentation } from './useProjectStatusPresentation';
import { useScopedProjectFilter } from './useScopedProjectFilter';
import type {
  EntityTrendsPageConfig,
  EntityTrendsPageViewModel,
} from './useEntityTrendsPage.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult } from '@flowqa/shared';

export type { EntityStatusKey, EntityTrendsPageViewModel } from './useEntityTrendsPage.types';
export const useEntityTrendsPage = <TEntityItem, TSummary extends { total: number }>(
  config: EntityTrendsPageConfig<TEntityItem, TSummary>,
): EntityTrendsPageViewModel<TEntityItem> => {
  const loadExecutionMetrics = config.loadExecutionMetrics ?? true;
  const [searchParams] = useSearchParams();
  const forcedLoadState = searchParams.get('loadState');
  const showForcedLoadingState = forcedLoadState === 'loading';
  const [metricsQueriesEnabled, setMetricsQueriesEnabled] = useState(false);
  const {
    range,
    activeTab,
    chartTab,
    suppressChartAnimation,
    searchValue,
    debouncedSearchValue,
    selectedStatuses,
    selectedTags,
    rangeStartIso,
    rangeEndIso,
    previousRangeStartIso,
    previousRangeEndIso,
    selectedStartDay,
    selectedEndDay,
    chartSelectedStartDay,
    chartSelectedEndDay,
    setSelectedStartDay,
    setSelectedEndDay,
    setChartSelectedStartDay,
    setChartSelectedEndDay,
    clearSelectedAndChartDates,
    isLatestMode,
    isAllMode,
    showDelta,
    hasCustomDates,
    isUntestedOnly,
    summaryInput,
    previousSummaryInput,
    summaryFiltersKey,
    previousSummaryFiltersKey,
    relatedEntitySummaryInput,
    relatedEntitySummaryFiltersKey,
    entityTableFilters,
    baseExecutionFilters,
    executionStatusFilters,
    executionStatusSelection,
    metricsRange,
    handleRangeChange,
    handleSearchChange,
    handleChartTabChange,
    handleActiveTabChange,
    handleStatusToggle,
    handleStatusesChange,
    handleTagsChange,
  } = useEntityTrendsQueryModel({
    gridPath: config.gridPath,
    entitySearchField: config.entitySearchField,
    executionSearchField: config.executionSearchField,
  });
  const deltasEnabled = true;
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectOptions,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { uploadProjectIdFieldId, uploadFlow } = useEntityTrendsUploadFlow({
    config,
    selectedOrganisationId,
    selectedProjectId: effectiveProjectId,
  });
  const { thresholds } = useProjectMetricThresholds(effectiveProjectId);
  const entityThresholds = config.selectThresholds(thresholds);
  const { groupErroredWithFailed, statusPresentationLoading } = useProjectStatusPresentation();
  const dataQueriesEnabled = isAuthenticated && !statusPresentationLoading;

  useEffect(() => {
    if (!isAuthenticated) {
      setMetricsQueriesEnabled(false);
      return;
    }
    if (!metricsQueriesEnabled) {
      setMetricsQueriesEnabled(true);
    }
    return undefined;
  }, [isAuthenticated, metricsQueriesEnabled]);

  const {
    summary,
    previousSummary,
    counts,
    previousCounts,
    deltas,
    metricCardsLoading,
    summaryIsFetching,
    summaryIsLoading,
  } = useEntityTrendsSummary<TEntityItem, TSummary>({
    config,
    isLatestMode,
    selectedProjectId: effectiveProjectId,
    selectedOrganisationId: selectedOrganisationId ?? null,
    isAuthenticated: dataQueriesEnabled,
    summaryInput,
    previousSummaryInput,
    summaryFiltersKey,
    previousSummaryFiltersKey,
    showDelta,
  });
  const relatedEntitySummaryQuery = useQuery<TSummary>({
    queryKey: config.summaryKey(
      effectiveProjectId ?? null,
      selectedOrganisationId ?? null,
      relatedEntitySummaryFiltersKey,
    ),
    enabled: dataQueriesEnabled,
    queryFn: async () =>
      config.queryLatestSummary(
        effectiveProjectId,
        selectedOrganisationId ?? undefined,
        relatedEntitySummaryInput,
      ),
  });
  const totalSecondaryValue = formatRelatedEntitySummaryTotalLabel(
    relatedEntitySummaryQuery.data?.total,
    config.metricsScope,
  );
  const groupedStatusOptions = useMemo(
    () => collapseStatusOptions(config.statusOptions, groupErroredWithFailed),
    [config.statusOptions, groupErroredWithFailed],
  );
  const statusFilterOptions = useMemo(
    () =>
      groupedStatusOptions.some((option) => option.value === 'untested')
        ? groupedStatusOptions
        : [...groupedStatusOptions, { value: 'untested' as const, label: 'Untested' }],
    [groupedStatusOptions],
  );
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;

  const executionMetricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: executionStatusSelection.length > 0 ? executionStatusSelection : [],
    tags: selectedTags,
    scope: config.metricsScope,
    projectId: effectiveProjectId,
    latestOnly: isLatestMode,
    name: debouncedSearchValue,
    startDay: isLatestMode ? null : hasCustomDates ? selectedStartDay : null,
    endDay: isLatestMode ? null : hasCustomDates ? selectedEndDay : null,
    enabled: loadExecutionMetrics && metricsQueriesEnabled && dataQueriesEnabled,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: executionStatusSelection.length > 0 ? executionStatusSelection : [],
    tags: selectedTags,
    scope: config.metricsScope,
    projectId: effectiveProjectId,
    name: debouncedSearchValue,
    startDay: showDelta ? previousStartDay : null,
    endDay: showDelta ? previousEndDay : null,
    enabled: loadExecutionMetrics && metricsQueriesEnabled && dataQueriesEnabled && showDelta,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });

  const {
    metricCards,
    metricValueItems,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
  } = useEntityTrendsMetricDisplay({
    statusOptions: groupedStatusOptions,
    metricsScope: config.metricsScope,
    counts,
    deltas,
    summary: summary ?? null,
    previousCounts,
    previousSummary: previousSummary ?? null,
    summaryIsFetching,
    showDelta,
    loadExecutionMetrics,
    executionMetrics: executionMetricsQuery.data ?? null,
    previousMetrics: previousMetricsQuery.data ?? null,
    entityThresholds,
    totalSecondaryValue,
  });

  const stackedChartData = useMemo(() => {
    if (!loadExecutionMetrics) return null;
    const baseData = buildStackedExecutionChartData(executionMetricsQuery.data);
    if (!baseData) return null;
    const collapsedData = groupErroredWithFailed
      ? collapseGroupedStackedChartData(baseData)
      : baseData;
    if (!collapsedData) return null;
    if (!isUntestedOnly) return collapsedData;
    const emptyData = collapsedData.data.map((datum) => ({
      ...datum,
      passed: 0,
      failed: 0,
      errored: 0,
      skipped: 0,
      untested: 0,
    }));
    return { ...collapsedData, data: emptyData };
  }, [executionMetricsQuery.data, groupErroredWithFailed, isUntestedOnly, loadExecutionMetrics]);

  const chartDates = useMemo(() => {
    if (!loadExecutionMetrics) return [];
    const labels = executionMetricsQuery.data?.metricsRangeLabels ?? [];
    const dates = executionMetricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [
    executionMetricsQuery.data?.metricsRangeDates,
    executionMetricsQuery.data?.metricsRangeLabels,
    loadExecutionMetrics,
  ]);

  const chartFilterRange = useMemo(() => {
    if (!isLatestMode || !chartSelectedStartDay) return null;
    return { start: chartSelectedStartDay, end: chartSelectedEndDay ?? chartSelectedStartDay };
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode]);
  const chartFilterIndices = useMemo(
    () => buildChartFilterIndices(chartDates, chartFilterRange),
    [chartDates, chartFilterRange],
  );
  const filteredStackedChartData = useMemo(() => {
    if (!stackedChartData || !chartFilterIndices) return stackedChartData;
    const filteredData = filterChartRowsByIndices(stackedChartData.data, chartFilterIndices);
    if (!filteredData) return stackedChartData;
    return { ...stackedChartData, data: filteredData };
  }, [chartFilterIndices, stackedChartData]);

  const { startDay: metricsStartDay, endDay: metricsEndDay } = useMemo(
    () => resolveChartDateBounds(chartDates),
    [chartDates],
  );
  const allModeExecutionDateFilters = useMemo(
    () =>
      isAllMode && metricsStartDay && metricsEndDay
        ? buildDateRangeFilters(
            'timestamp',
            `${metricsStartDay}T00:00:00.000Z`,
            `${metricsEndDay}T23:59:59.999Z`,
          )
        : undefined,
    [isAllMode, metricsEndDay, metricsStartDay],
  );
  const executionFilters = useMemo(
    () => mergeFilters(baseExecutionFilters, allModeExecutionDateFilters),
    [allModeExecutionDateFilters, baseExecutionFilters],
  );
  const pickerStartDay = useMemo(
    () =>
      selectedStartDay ??
      (range !== 'latest' && range !== 'all'
        ? (rangeStartIso?.slice(0, 10) ?? null)
        : metricsStartDay),
    [metricsStartDay, range, rangeStartIso, selectedStartDay],
  );
  const pickerEndDay = useMemo(
    () =>
      selectedEndDay ??
      (range !== 'latest' && range !== 'all' ? (rangeEndIso?.slice(0, 10) ?? null) : metricsEndDay),
    [metricsEndDay, range, rangeEndIso, selectedEndDay],
  );

  const handleSelectedStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleSelectedEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const handleChartDateClear = useCallback(() => {
    clearSelectedAndChartDates();
  }, [clearSelectedAndChartDates]);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      if (!day) return;
      if (isLatestMode) {
        if (chartSelectedStartDay) {
          setChartSelectedStartDay(null);
          setChartSelectedEndDay(null);
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setChartSelectedStartDay(day);
          setChartSelectedEndDay(day);
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      } else {
        const isSameDay = selectedStartDay === day && selectedEndDay === day;
        if (isSameDay) {
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      }
    },
    [chartDates, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay],
  );

  const chartHeight = 360;
  const showChartSkeleton =
    loadExecutionMetrics &&
    (showForcedLoadingState ||
      (metricsQueriesEnabled &&
        executionMetricsQuery.data === undefined &&
        !executionMetricsQuery.isError &&
        !executionMetricsQuery.isFetched));
  const showChartErrorState = loadExecutionMetrics && executionMetricsQuery.isError;
  const lineChartLabels = executionMetricsQuery.data?.metricsRangeLabels ?? [];
  const passRateChartData = useMemo(() => {
    if (!loadExecutionMetrics) return null;
    return buildLineChartDataOrFallback({
      series: executionMetricsQuery.data?.series.filteredPassRate,
      labels: lineChartLabels,
      dates: chartDates,
      key: 'passRate',
      isFetched: executionMetricsQuery.isFetched,
      hasError: executionMetricsQuery.isError,
    });
  }, [
    chartDates,
    executionMetricsQuery.data?.series.filteredPassRate,
    executionMetricsQuery.isError,
    executionMetricsQuery.isFetched,
    lineChartLabels,
    loadExecutionMetrics,
  ]);
  const frequencyChartData = useMemo(() => {
    if (!loadExecutionMetrics) return null;
    return buildLineChartDataOrFallback({
      series: executionMetricsQuery.data?.series.executionTrendMetrics,
      labels: lineChartLabels,
      dates: chartDates,
      key: 'frequency',
      isFetched: executionMetricsQuery.isFetched,
      hasError: executionMetricsQuery.isError,
    });
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionTrendMetrics,
    executionMetricsQuery.isError,
    executionMetricsQuery.isFetched,
    lineChartLabels,
    loadExecutionMetrics,
  ]);
  const durationChartData = useMemo(() => {
    if (!loadExecutionMetrics) return null;
    return buildLineChartDataOrFallback({
      series: executionMetricsQuery.data?.series.executionDurationAverage,
      labels: lineChartLabels,
      dates: chartDates,
      key: 'duration',
      isFetched: executionMetricsQuery.isFetched,
      hasError: executionMetricsQuery.isError,
    });
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionDurationAverage,
    executionMetricsQuery.isError,
    executionMetricsQuery.isFetched,
    lineChartLabels,
    loadExecutionMetrics,
  ]);
  const filteredPassRateChartData = useMemo(
    () => filterSeriesByIndices(passRateChartData, chartFilterIndices),
    [chartFilterIndices, passRateChartData],
  );
  const filteredFrequencyChartData = useMemo(
    () => filterSeriesByIndices(frequencyChartData, chartFilterIndices),
    [chartFilterIndices, frequencyChartData],
  );
  const filteredDurationChartData = useMemo(
    () => filterSeriesByIndices(durationChartData, chartFilterIndices),
    [chartFilterIndices, durationChartData],
  );
  const showLineChartPoints = useMemo(
    () =>
      shouldShowSingleDayChartPoints({
        isLatestMode,
        chartSelectedStartDay,
        chartSelectedEndDay,
        selectedStartDay,
        selectedEndDay,
      }),
    [chartSelectedEndDay, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay],
  );

  const queryKey = useMemo(
    () =>
      queryKeys.dataGrid(
        config.gridPath,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        showDelta ? 'table-deltas-on' : 'table-deltas-off',
      ),
    [
      config.gridPath,
      previousRangeEndIso,
      previousRangeStartIso,
      rangeEndIso,
      rangeStartIso,
      showDelta,
    ],
  );
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TEntityItem>> =>
      config.queryEntityTable(input, {
        selectedProjectId: effectiveProjectId,
        selectedOrganisationId: selectedOrganisationId ?? null,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      }),
    [
      config,
      deltasEnabled,
      previousRangeEndIso,
      previousRangeStartIso,
      rangeEndIso,
      rangeStartIso,
      selectedOrganisationId,
      effectiveProjectId,
    ],
  );

  const forcedPageLoadState = searchParams.get('pageLoadState');
  const showPageSkeleton =
    forcedPageLoadState === 'loading' ||
    statusPresentationLoading ||
    (isAuthenticated && summaryIsLoading && !summary);
  const showMetricCardsSkeleton = showForcedLoadingState || (isAuthenticated && metricCardsLoading);
  const isKnownEmpty = !showForcedLoadingState && summary !== null && summary.total === 0;

  return {
    loadingMessage: config.loadingMessage,
    groupErroredWithFailed,
    isKnownEmpty,
    range,
    chartTab,
    activeTab,
    searchValue,
    debouncedSearchValue,
    pickerStartDay,
    pickerEndDay,
    hasCustomDates,
    isLatestMode,
    metricCards,
    selectedStatuses,
    statusFilterOptions,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    metricValueItems,
    chartHeight,
    showMetricCardsSkeleton,
    showChartSkeleton,
    showChartErrorState,
    suppressChartAnimation,
    showLineChartPoints,
    filteredStackedChartData,
    filteredPassRateChartData,
    filteredFrequencyChartData,
    filteredDurationChartData,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
    deltasEnabled,
    selectedOrganisationId: selectedOrganisationId ?? null,
    rangeStartIso,
    rangeEndIso,
    previousRangeStartIso,
    previousRangeEndIso,
    queryKey,
    queryFn,
    entityTableFilters,
    executionFilters,
    executionStatusFilters,
    showChartDateClear: hasCustomDates,
    showForcedLoadingState,
    showPageSkeleton,
    selectedProjectId: effectiveProjectId,
    projectOptions,
    selectedProjects,
    projectFilterOptions,
    showProjectFilter,
    uploadProjectIdFieldId,
    uploadFlow,
    handleRangeChange,
    handleSearchChange,
    handleChartTabChange,
    handleActiveTabChange,
    handleStatusToggle,
    handleStatusesChange,
    handleTagsChange,
    handleProjectsChange,
    handleChartSelect,
    handleSelectedStartDayChange,
    handleSelectedEndDayChange,
    handleChartDateClear,
  };
};
